/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// Component imports
import { Checkbox, Col, Form, Input, Radio, Row, Select, Typography } from 'antd';
import RichTextEditor from '../../../Forms/Fields/RichTextEditor/RichTextEditor';
import VariableSelectItem from '../../../UI/VariableSelectItem';

// Misc imports
import { VARIABLE_TYPES } from '../../../../constants/main';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import { RICH_TEXT_INLINE_ACTIONS, RICH_TEXT_TOOLBAR_ACTIONS } from '../../../../constants/markdown';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import groupBy from '../../../../utils/array/groupBy';

// Constant declarations
const { Text } = Typography;
const DEFAULT_CATEGORY = 'Other';

const VariableNodeForm = ({ form, setCanSubmit }) => {
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();

  const { data: storyVariables, isFetching: isFetchingStoryVariables } = getCurrentQueryState('fetchStoryVariables', { orgId, tenantId });

  const [localVariables, setLocalVariables] = useState([]); // Only for use within the Variable node, other nodes should access the storyVariables directly

  const { isEditingPrimaryLanguage } = useBotLanguage();

  // Whenever the story variables change, update the local variables
  useEffect(() => {
    const validVariables = [...storyVariables.filter(variable => !variable.isConstant)];
    setLocalVariables(validVariables);
  }, [storyVariables]);

  const updateVariables = (newVariableName) => {
    if (newVariableName && storyVariables.filter(variable => variable.name === newVariableName).length === 0) {
      setLocalVariables([...new Set([...storyVariables.filter(variable => !variable.isConstant), { name: newVariableName, type: '' }])]);
    }
  };

  return (
    <Fragment>
      <Row className="mb-2">
        <Col xs={24}>
          <Form.Item
            name={['data', 'variable', 'key']}
            label={
              <Row>
                <Col span={24}>
                  <Text>
                    {t('Name')}
                  </Text>
                </Col>

                <Col span={24} className="mb-1">
                  <Text type="secondary">
                    {t('Select a variable or type to create custom.')}
                  </Text>
                </Col>
              </Row>
            }
            rules={[
              {
                required: true,
                message: t('Key cannot be blank.')
              },
              {
                pattern: /^[a-zA-Z0-9_$\\.]*$/,
                message: t('Variable names can only use letters, numbers, underscores, dollar signs, and dots.')
              },
              {
                validator: (_rule, value) => {
                  const trimmed = value?.trim();
                  if (!trimmed) {
                    return Promise.resolve();
                  }

                  if (storyVariables?.some(variable => variable.name === trimmed && variable.isConstant)) {
                    return Promise.reject();
                  }

                  return Promise.resolve();
                },
                validateTrigger: ['onChange', 'onBlur']
              }
            ]}
          >
            {
              storyVariables && localVariables ? (
                <Select
                  size="large"
                  disabled={!isEditingPrimaryLanguage}
                  showSearch
                  placeholder={t('Type / select variable')}
                  // When the user types a new variable name, add it to the list of local variables
                  onSearch={updateVariables}
                  getPopupContainer={triggerNode => triggerNode.parentElement}
                  onBlur={(e) => updateVariables(e.target.value)}
                  loading={isFetchingStoryVariables}
                  filterOption={(input, option) => {
                    // Filter Option iterates through the options, find the current option to get the name and the description data
                    const currentVariable = localVariables.find((variable) => variable.name === option.key);
                    return currentVariable?.name.toLowerCase().includes(input.toLowerCase()) || currentVariable?.description?.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {
                    Object.entries(groupBy(localVariables?.filter((item, index, self) => self.findIndex(t => t.name === item.name) === index),
                      item => item.type || DEFAULT_CATEGORY))
                      .map(([groupName, items]) => {
                        // Apply filtering before rendering the group
                        const filteredItems = items.filter((variable) => {
                          return ![VARIABLE_TYPES.QUESTION, VARIABLE_TYPES.SYSTEM, VARIABLE_TYPES.LIVECHAT, VARIABLE_TYPES.CSAT].includes(variable.type);
                        });

                        // Only render the group if it has items after filtering
                        return filteredItems.length > 0 ? (
                          <Select.OptGroup key={groupName} label={t(groupName || DEFAULT_CATEGORY, { context: 'variable type' })}>
                            {
                              filteredItems.map(variable => (
                                <Select.Option
                                  key={variable.name}
                                  value={variable.name}
                                  name={variable.name}
                                >
                                  <VariableSelectItem
                                    variable={variable}
                                    showDescription={true}
                                    showActivity={false}
                                    truncateCount={0}
                                    truncateDescriptionCount={45}
                                  />
                                </Select.Option>
                              ))
                            }
                          </Select.OptGroup>
                        ) : null;
                      })
                  }
                </Select>
              ) : null
            }
          </Form.Item>
        </Col>
      </Row>

      <Row className="mb-2">
        <Col xs={24}>
          <Form.Item
            name={['data', 'variable', 'value']}
            rules={[
              {
                required: true,
                message: t('Value cannot be blank.')
              }]}
            label={
              <Row>
                <Col span={24}>
                  <Text>
                    {t('Value')}
                  </Text>
                </Col>

                <Col span={24} className="mb-1">
                  <Text type="secondary">
                    {t('Enter a value for the variable or select an existing variable as its value.')}
                  </Text>
                </Col>
              </Row>
            }
          >
            <RichTextEditor
              toolbarActions={[RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE]}
              placeholder={t('type value or "/" to insert expression')}
              className="ql-md ql-border-sm"
              hasFloatingToolbar={true}
              handleAsPlainText={true}
              style={{ maxHeight: 250 }}
              inlineAction={
                {
                  value: RICH_TEXT_INLINE_ACTIONS.EXPRESSIONS.key,
                  active: form.getFieldValue(['data', 'variable', 'isExpression'])
                }
              }
              onInlineActionChange={(value) => {
                if(value === form.getFieldValue(['data', 'variable', 'isExpression'])) {
                  return;
                }

                if(!value && form.getFieldValue(['data', 'variable', 'isExpression']) === null) {
                  return;
                }

                if(value === form.getFieldValue(['data', 'variable', 'isExpression'])) {
                  return;
                }

                form.setFieldValue(['data', 'variable', 'isExpression'], value);
                setCanSubmit(true);
              }}
              disabled={!isEditingPrimaryLanguage}
            />
          </Form.Item>

          {/* checkbox that says "Treat as expression", value called isExpression. */}
          <Form.Item
            hidden
            disabled={!isEditingPrimaryLanguage}
            name={['data', 'variable', 'isExpression']}
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24} className="mb-2">
        <Col xs={12}>
          <Form.Item
            name={['data', 'variable', 'description']}
            label={
              <Row>
                <Col span={24}>
                  <Text>
                    {t('Description')}
                  </Text>
                </Col>

                <Col span={24} className="mb-1">
                  <Text type="secondary">
                    {t('Describe the variable.')}
                  </Text>
                </Col>
              </Row>
            }
          >
            <Input.TextArea
              size="large"
              disabled={!isEditingPrimaryLanguage}
              autoSize={{
                minRows: 2,
                maxRows: 4
              }}
              placeholder={t('Type description')}
            />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name={['data', 'variable', 'type']}
            rules={[
              {
                required: true,
                message: t('Type cannot be blank.')
              }]}
            label={
              <Row>
                <Col span={24}>
                  <Text>
                    {t('Storage Type')}
                  </Text>
                </Col>
                <Col span={24} className="mb-1">
                  <Text type="secondary">
                    {t('Choose how to store this variable.')}
                  </Text>
                </Col>
              </Row>
            }
          >
            <Radio.Group
              disabled={!isEditingPrimaryLanguage}
              className="d-flex flex-row"
            >
              <Radio
                key="session"
                value="SESSION"
              >
                {t('Session')}
              </Radio>

              <Radio
                key="contact"
                value="CONTACT"
              >
                {t('Contact')}
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
    </Fragment>
  );
};

VariableNodeForm.propTypes = {
  form: PropTypes.object.isRequired,
  setCanSubmit: PropTypes.func.isRequired
};

export default VariableNodeForm;
